import type { RowData } from "../sidebar/types";
import type { FilesystemJs, FolderNotificationStateJs, FolderRowJs, InvitationJs, OrganizationJs, RecentJs, ShortcutJs } from "@/generated/models";
import { sort } from "fast-sort";
import { isFilesystemJs, isFolderRowJs, isInvitationJs, isOrganizationJs, isRecentJs, isShortcutJs, OrganizationJsSchema } from "ls/common/entity-schemas";
import { naturalSort, naturalSortCompare } from "ls/common/lib/natural-sort";
import { isNoItems, isSubRowsHash } from "ls/common/table-schemas";
import * as v from "valibot";
import { jsDateFromAspDateString as toJsDate } from "@/common/lib";
import { isObject } from "@/common/lib/guards";
import { FilesystemType } from "@/generated/models";
import { isCustomGroup } from "../sidebar/types";

export function isFolderNotificationState(node: unknown): node is FolderNotificationStateJs {
  return isObject(node) && "NewCommentShortcut" in node
    && node.NewCommentShortcut !== undefined;
}
export const noSharedWithMeWorkspacesSchema = v.object({
  id: v.literal("no-shared-with-me-workspaces"),
});
export type NoSharedWithMeWorkspaces = v.InferOutput<typeof noSharedWithMeWorkspacesSchema>;
export const isNoSharedWithMeWorkspaces = (row: unknown) => v.is(noSharedWithMeWorkspacesSchema, row);

export const createOrganizationWorkspacesSchema = v.object({
  id: v.string(),
  kind: v.literal("create-organization-workspaces"),
  organization: OrganizationJsSchema,
});
export type CreateOrganizationWorkspaces = v.InferOutput<typeof createOrganizationWorkspacesSchema>;
export const isCreateOrganizationWorkspaces = (row: unknown) => v.is(createOrganizationWorkspacesSchema, row);

export const suggestedWorkspacesSchema = v.object({
  id: v.string(),
  kind: v.literal("suggested-workspaces"),
  organization: OrganizationJsSchema,
});
export type SuggestedWorkspaces = v.InferOutput<typeof suggestedWorkspacesSchema>;
export const isSuggestedWorkspaces = (row: unknown) => v.is(suggestedWorkspacesSchema, row);

export const noOrganizationWorkspacesSchema = v.object({
  id: v.string(),
  kind: v.literal("no-organization-workspaces"),
});
export type NoOrganizationWorkspaces = v.InferOutput<typeof noOrganizationWorkspacesSchema>;
export const isNoOrganizationWorkspaces = (row: unknown) => v.is(noOrganizationWorkspacesSchema, row);

export function getName(node: OrganizationJs | FilesystemJs | FolderRowJs | ShortcutJs | RecentJs | InvitationJs): string {
  if (isShortcutJs(node)) return node.Name;
  if (isFolderRowJs(node)) return node.FolderName;
  if (isFilesystemJs(node)) return node.DisplayName;
  if (isRecentJs(node)) return getName(node.Shortcut);
  if (isInvitationJs(node)) return node.FilesystemName;
  return node.Name;
}

export function getRowName(node: RowData) {
  if (isSubRowsHash(node) || isCreateOrganizationWorkspaces(node) || isNoSharedWithMeWorkspaces(node) || isNoOrganizationWorkspaces(node) || isSuggestedWorkspaces(node)) return "";
  if (isNoItems(node)) return "Nothing to show here";
  if (isCustomGroup(node)) return node.name;
  return getName(node);
}

export function getId(node: OrganizationJs | FilesystemJs | FolderRowJs | ShortcutJs | RecentJs | InvitationJs): string {
  if (isShortcutJs(node)) return node.Shortcut;
  if (isFolderRowJs(node)) return node.FolderId;
  if (isFilesystemJs(node)) return node.FilesystemId;
  if (isRecentJs(node)) return getId(node.Shortcut);
  if (isInvitationJs(node)) return node.FilesystemId;
  return node.Id;
}

const nextNoItemsId = (() => {
  let start = 0;

  return () => {
    return (start++).toString();
  };
})();

export function getRowId(node: RowData) {
  if (isNoItems(node)) {
    return nextNoItemsId();
  }
  if (isNoSharedWithMeWorkspaces(node)
    || isCreateOrganizationWorkspaces(node)
    || isNoOrganizationWorkspaces(node)
    || isSuggestedWorkspaces(node)
    || isSubRowsHash(node)
    || isCustomGroup(node)
  ) {
    return node.id;
  }
  return getId(node);
}

export function getLastModified(node: ShortcutJs | FolderRowJs) {
  return isShortcutJs(node) ? getShortcutJsLastModified(node) : getFolderRowJsLastModified(node);
}

export function getShortcutJsLastModified(shortcut: ShortcutJs) {
  return {
    updatedAt: shortcut.ModifiedOn ? toJsDate(shortcut.ModifiedOn) : toJsDate(shortcut.CreatedOn),
    updatedBy: shortcut.ModifiedBy,
  };
}

export function getFolderRowJsLastModified(folder: FolderRowJs) {
  return {
    updatedAt: folder.ModifiedOn && folder.ModifiedOn.Value ? toJsDate(folder.ModifiedOn.Value) : toJsDate(folder.CreatedOn),
    updatedBy: folder.ModifiedOn.Username,
  };
}

export function workspacesSort(rows: FilesystemJs[]) {
  return naturalSort(rows).asc(d => d.DisplayName);
}

export function filesystemSort<T extends OrganizationJs | FilesystemJs | FolderRowJs | ShortcutJs>(rows: T[]): T[] {
  return sort(rows).by([
    { desc: r => isOrganizationJs(r) },
    { desc: r => isFilesystemJs(r) },
    { desc: r => isFolderRowJs(r) },
    { desc: r => isShortcutJs(r) },
    { asc: r => getName(r), comparer: naturalSortCompare },
  ]);
}

export function isRegularWorkspace(workspace: FilesystemJs) {
  return isRegularWorkspaceType(workspace.FilesystemType);
}

export function isTemplateWorkspace(workspace: FilesystemJs) {
  return isTemplateWorkspaceType(workspace.FilesystemType);
}

export function isRegularWorkspaceType(workspaceType: FilesystemType) {
  return workspaceType === FilesystemType.Regular;
}

export function isTemplateWorkspaceType(workspaceType: FilesystemType) {
  return workspaceType === FilesystemType.Template;
}

export function isLibraryWorkspace(workspace: FilesystemJs) {
  return isLibraryWorkspaceType(workspace.FilesystemType);
}

export function isLibraryWorkspaceType(workspaceType: FilesystemType) {
  return workspaceType === FilesystemType.Library;
}

export function isSharedAsset(workspace: FilesystemJs) {
  return isSharedAssetType(workspace.FilesystemType);
}

export function isSharedAssetType(workspaceType: FilesystemType) {
  return isTemplateWorkspaceType(workspaceType) || isLibraryWorkspaceType(workspaceType);
}
